import { clone, isEqual } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';

import {
    Component, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';

import { PickerTranslations } from '@shared/utils/translations';
import { BaseControlValueAccessor } from './base-control-value-accessor';

@Component({
    selector: 'syn-picker',
    templateUrl: './picker.component.html',
    styleUrls: ['./picker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => PickerComponent),
            multi: true
        }
    ],
    encapsulation: ViewEncapsulation.None,
})
export class PickerComponent<T> extends BaseControlValueAccessor<T> implements ControlValueAccessor, OnInit, OnDestroy {

    @Input() translations: PickerTranslations;
    @Input() placeholder: string;
    @Input() contentLoader: Subject<{ term: string; strict?: boolean }>;
    @Input() content: Observable<T[]>;
    @Input() itemTemplate: TemplateRef<any>;
    @Input() newItemTemplate: TemplateRef<any>;
    @Input() latterTemplate: TemplateRef<any>;
    @Input() errorMessage: string;
    @Input() required: object;
    @Input() strictSearch: boolean;
    @Input() loadMore: boolean;
    @Input() disabledPick: boolean;
    @Input() paddingLeft = 1;
    @Input() subscriptSizing: string;

    @Output() creationEvent = new EventEmitter<string>();
    @Output() change = new EventEmitter<T>();
    @Output() loadMoreContentPicker = new EventEmitter<T>();
    @ViewChild('select', { static: true }) select: MatSelect;

    public filteredResults: T[] = [];
    public allResults: T[] = [];
    public fetching = false;
    public fetchedOnce = false;
    public selectedValue: T;
    public show = 10;
    private content$: Subscription;
    private lastTerm = '';

    constructor() {
        super();
    }

    public async ngOnInit(): Promise<void> {
        this.setDisabledState(this.disabled);

        const isObs = this.content && typeof (this.content).subscribe === 'function';
        const content$ = isObs ? this.content : new Observable<T[]>(observer => {
            observer.next((this.content as unknown as T[]) || []);
            observer.complete();
        });

        this.content$ = content$.subscribe(results => {
            this.fetching = false;
            this.allResults = results;
            this.filteredResults = results;
        });

        this.select.openedChange.subscribe((state) => {
            if (state) {
                if ((!this.filteredResults.length && this.lastTerm === '') || !this.fetchedOnce) {
                    this.load('');
                }
            } else {
                if (!this.select.value && !this.selectedValue) {
                    this.writeValue(null);
                } else if (!this.select.value && this.selectedValue) {
                    this.select.value = this.selectedValue;
                } else if (this.select.value && this.selectedValue) {
                    this.writeValue(this.selectedValue);
                }
            }
        });
    }

    public ngOnDestroy(): void {
        this.content$.unsubscribe();
    }

    public onCreateObject($event: MatOptionSelectionChange): void {
        if ($event.source && $event.source.selected) {
            this.creationEvent.emit(this.lastTerm);
            this.writeValue(null);
        }
    }

    public deselectObject($event): void {
        this.writeValue(null);
        $event.stopPropagation();
    }

    public load(term: string): void {
        this.lastTerm = term?.toLowerCase() || '';
        this.fetching = true;
        this.fetchedOnce = true;

        if (this.contentLoader) {
            this.contentLoader.next({ term, strict: this.strictSearch ?? false });
            return;
        }

        if (this.allResults) {
            const t = this.lastTerm.trim();
            this.filteredResults = t
                ? this.allResults.filter(item =>
                    JSON.stringify(item).toLowerCase().includes(t)
                )
                : this.allResults;
        }

        this.fetching = false;
    }

    public writeValue(object: T): void {
        this.selectedValue = object;
        this.value = clone(object);
        this.onChange(this.value);
        this.onTouched();
        this.change.emit(this.value);

        // Problem: when we change mat-select value, he need to have the right object in stock to display it
        // If there is no corresponding object, nothing is displayed inside mat-select input
        // So when a setValue happened on the control, we check if the value is already in stock
        // If not, we push it at the beginning so it is within the visible slice
        // If yes, we move it to the beginning so mat-select can find it within the slice:0:show range
        const index = this.filteredResults.findIndex(element => isEqual(element, object));
        if (object && (!this.filteredResults.length || index === -1)) {
            this.filteredResults.unshift(object);
        } else if (object && index !== -1) {
            this.filteredResults.splice(index, 1);
            this.filteredResults.unshift(object);
        }

    }

    public compareFn(o1: T, o2: T): boolean {
        return o1 && o2 ? Object.is(o1, o2) : o1 === o2;
    }

    public setDisabledState(isDisabled: boolean): void {
        if (this.select) {
            this.select.disabled = isDisabled;
        }
    }

    public toggleLoader() {
        this.show += 10;
    }
}
